import React, { useState } from 'react';
import QrScanner from 'react-qr-scanner';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';

interface QRCodeScannerProps {
    onClose: () => void;
}

export const QRCodeScanner: React.FC<QRCodeScannerProps> = ({ onClose }) => {
    const [scanning, setScanning] = useState(true);
    const [chestNumber, setChestNumber] = useState<number | null>(null);
    const [error, setError] = useState<string>("");

    const handleScan = (data: any) => {
        if (data && scanning) {
            try {
                const scannedValue = data.text || data;
                const checkInCode = parseInt(scannedValue, 10);

                if (isNaN(checkInCode)) {
                    setError("Invalid QR code: Not a number");
                    return;
                }

                // Validate: must be divisible by 4
                if (checkInCode % 6 !== 0) {
                    setError(`Invalid check-in code: ${checkInCode} is not divisible by 6`);
                    return;
                }

                // Calculate chest number
                const calculatedChestNumber = checkInCode / 6;
                // Append 3 to the beginning
                const finalChestNumber = parseInt(`5${calculatedChestNumber}`);
                setChestNumber(finalChestNumber);
                setScanning(false);
                setError("");
            } catch (err) {
                setError("Error processing QR code");
            }
        }
    };

    const handleError = (err: any) => {
        console.error(err);
        setError("Camera error: Please ensure camera permissions are granted");
    };

    const handleScanAgain = () => {
        setScanning(true);
        setChestNumber(null);
        setError("");
    };

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    actions={
                        <Button onClick={onClose} variant="link">
                            Close
                        </Button>
                    }
                >
                    QR Code Scanner
                </Header>
            }
        >
            <SpaceBetween direction="vertical" size="l">
                {error && (
                    <Alert type="error" dismissible onDismiss={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {scanning ? (
                    <div style={{ 
                        width: '100%',
                        maxWidth: '500px', 
                        margin: '0 auto'
                    }}>
                        <QrScanner
                            delay={300}
                            onError={handleError}
                            onScan={handleScan}
                            style={{ width: '100%' }}
                            constraints={{
                                video: { facingMode: 'environment' }
                            }}
                        />
                        <Box textAlign="center" padding={{ top: 'm' }}>
                            <Box variant="p" color="text-body-secondary" fontSize="body-m">
                                Point your camera at the QR code
                            </Box>
                        </Box>
                    </div>
                ) : (
                    <div style={{ 
                        textAlign: 'center',
                        padding: '32px 16px'
                    }}>
                        <SpaceBetween direction="vertical" size="l">
                            <Box>
                                <div style={{ 
                                    fontSize: '72px',
                                    fontWeight: 'bold',
                                    color: '#037f0c',
                                    lineHeight: '1.2',
                                    marginBottom: '8px'
                                }}>
                                    {chestNumber}
                                </div>
                                <Box variant="p" color="text-body-secondary" fontSize="heading-m">
                                    Chest Number
                                </Box>
                            </Box>
                            <Button onClick={handleScanAgain} variant="primary" fullWidth>
                                Scan Again
                            </Button>
                        </SpaceBetween>
                    </div>
                )}
            </SpaceBetween>
        </Container>
    );
};
